/**
 * ============================================================================
 * NOTIFICATION SETTINGS COMPONENT
 * ============================================================================
 * User notification preferences – dashboard-wt theme
 */

import React, { useEffect, useState } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Switch from '../common/Switch';
import Spinner from '../common/Spinner';

/**
 * Notification Settings Component
 * @returns {JSX.Element}
 */
const NotificationSettings = () => {
  const { preferences, loadPreferences, updatePreferences, loading } = useNotifications();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();

  const [localPreferences, setLocalPreferences] = useState({
    email: true,
    sms: true,
    push: true,
    inApp: true,
    transactionUpdates: true,
    paymentNotifications: true,
    disputeAlerts: true,
    deliveryNotifications: true,
    systemUpdates: false,
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences().then((prefs) => {
      if (prefs) {
        setLocalPreferences((prev) => ({ ...prev, ...prefs }));
      }
    });
  }, [loadPreferences]);

  const handleToggle = (key) => {
    setLocalPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updatePreferences(localPreferences);
      showSuccess(
        language === 'fr'
          ? 'Préférences de notification mises à jour!'
          : 'Notification preferences updated!'
      );
    } catch (error) {
      showError(error.message || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalPreferences({
      email: true,
      sms: true,
      push: true,
      inApp: true,
      transactionUpdates: true,
      paymentNotifications: true,
      disputeAlerts: true,
      deliveryNotifications: true,
      systemUpdates: false,
    });
  };

  if (loading && !preferences) {
    return (
      <div className="flex items-center justify-center p-12 rounded-xl bg-[#111] border border-white/10">
        <Spinner size="lg" />
      </div>
    );
  }

  const notificationCategories = [
    {
      title: language === 'fr' ? 'Canaux de notification' : 'Notification Channels',
      description:
        language === 'fr'
          ? 'Choisissez comment vous souhaitez recevoir les notifications'
          : 'Choose how you want to receive notifications',
      settings: [
        { key: 'email', label: language === 'fr' ? 'Email' : 'Email', icon: '📧' },
        { key: 'sms', label: language === 'fr' ? 'SMS' : 'SMS', icon: '💬' },
        { key: 'push', label: language === 'fr' ? 'Notifications push' : 'Push Notifications', icon: '🔔' },
        { key: 'inApp', label: language === 'fr' ? 'Dans l\'application' : 'In-App', icon: '📱' },
      ],
    },
    {
      title: language === 'fr' ? 'Types de notifications' : 'Notification Types',
      description:
        language === 'fr'
          ? 'Contrôlez quels types de notifications vous recevez'
          : 'Control which types of notifications you receive',
      settings: [
        {
          key: 'transactionUpdates',
          label: language === 'fr' ? 'Mises à jour de transaction' : 'Transaction Updates',
          icon: '📊',
        },
        {
          key: 'paymentNotifications',
          label: language === 'fr' ? 'Notifications de paiement' : 'Payment Notifications',
          icon: '💰',
        },
        {
          key: 'disputeAlerts',
          label: language === 'fr' ? 'Alertes de litige' : 'Dispute Alerts',
          icon: '⚠️',
        },
        {
          key: 'deliveryNotifications',
          label: language === 'fr' ? 'Notifications de livraison' : 'Delivery Notifications',
          icon: '📦',
        },
        {
          key: 'systemUpdates',
          label: language === 'fr' ? 'Mises à jour système' : 'System Updates',
          icon: '⚙️',
        },
      ],
    },
  ];

  return (
    <div className="dashboard-wt space-y-6">
      {notificationCategories.map((category, categoryIndex) => (
        <div
          key={categoryIndex}
          className="rounded-xl bg-[#111] border border-white/10 p-5 animate-fadeInUp"
          style={{ animationDelay: `${categoryIndex * 0.1}s` }}
        >
          <div className="mb-4">
            <h3 className="text-lg font-bold text-[#fafafa] mb-1">
              {category.title}
            </h3>
            <p className="text-sm text-[#a3a3a3]">
              {category.description}
            </p>
          </div>
          <div className="space-y-1">
            {category.settings.map((setting, index) => (
              <div
                key={setting.key}
                className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors animate-fadeInUp"
                style={{ animationDelay: `${(categoryIndex * 0.1) + (index * 0.05)}s` }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl" aria-hidden>{setting.icon}</span>
                  <label className="text-sm font-semibold text-[#fafafa] cursor-pointer" htmlFor={`notif-${setting.key}`}>
                    {setting.label}
                  </label>
                </div>
                <Switch
                  id={`notif-${setting.key}`}
                  size="lg"
                  checked={localPreferences[setting.key]}
                  onChange={() => handleToggle(setting.key)}
                  variant={localPreferences[setting.key] ? 'success' : 'default'}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Actions - same green theme as rest of dashboard */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={handleReset}
          className="dashboard-wt-btn-secondary flex-1"
        >
          {language === 'fr' ? 'Réinitialiser' : 'Reset'}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="dashboard-wt-btn-primary flex-1 dashboard-wt-btn-large"
        >
          {saving ? (
            <span className="inline-flex items-center gap-2">
              <Spinner size="sm" />
              {language === 'fr' ? 'Enregistrement…' : 'Saving…'}
            </span>
          ) : (
            <>💾 {language === 'fr' ? 'Enregistrer' : 'Save Preferences'}</>
          )}
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;
